'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { AccordionItemContext } from './context/accordion-item.context';
import { AccordionRootStateContext } from './context/accordion-root.context';
import { accordionItemStyles } from './styles/accordion-item.styles';
import type { AccordionItemPropsInterface } from './types/accordion-item.types';

/**
 * A simple container used to wrap an `Accordion.Header` and `Accordion.Panel` set.
 *
 * @since 8.43.0
 */

export const AccordionItem = forwardRef<HTMLLIElement, AccordionItemPropsInterface>(
  (props: AccordionItemPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      disabled = false,
      value,
      ...rest
    } = props;

    const { expanded, expandedValues } = useContext(AccordionRootStateContext);

    const isExpanded = expandedValues
      ? expandedValues.has(value)
      : Array.isArray(expanded)
        ? expanded.includes(value)
        : expanded === value;

    const Component = asChild ? Slot : 'li';

    const styles = css(accordionItemStyles, cssProp);

    const contextValue = useMemo(
      () => ({
        disabled,
        expanded: isExpanded,
        value,
      }),
      [disabled, isExpanded, value],
    );

    return (
      <AccordionItemContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="Accordion.Item"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {children}
        </Component>
      </AccordionItemContext.Provider>
    );
  },
);

AccordionItem.displayName = 'Accordion.Item';
